import { useMemo } from 'react';
import type {
  DrawAllocationMode,
  DrawPreview,
  PlayEvent,
  PlayEventParticipant,
} from '@court-sync/shared';
import {
  flattenDrawPreviewMatches,
  getPlayEventParticipantDisplayName,
} from '@court-sync/shared';
import { PlayEventBracketTree } from '@/components/play/PlayEventBracketTree';
import type { DrawMatchListItem } from '@/components/play/PlayEventDrawMatchList';
import { buildPreviewDrawMatchListItems } from '@/lib/playEventDrawUtils';
import { cn } from '@/lib/utils';

interface PlayEventDrawReviewDiagramProps {
  event: PlayEvent;
  preview: DrawPreview;
  allocationMode: DrawAllocationMode;
  participantsById: Map<string, PlayEventParticipant>;
  participantNamesById: Record<string, string>;
  courtNamesById: Record<string, string>;
  courtAssignmentsByKey: Record<string, string | null>;
}

function getReviewSideLabel(
  match: DrawMatchListItem,
  side: 'a' | 'b',
  participantsById: Map<string, PlayEventParticipant>
): string {
  if (match.isBye && side === 'b') return 'Bye';
  const participantIds = side === 'a' ? match.sideAIds : match.sideBIds;
  if (participantIds.length > 0) {
    return participantIds
      .map((id) => {
        const participant = participantsById.get(id);
        return participant ? getPlayEventParticipantDisplayName(participant) : 'Unknown';
      })
      .join(' / ');
  }
  if (side === 'a' && match.sideAPlaceholder) return match.sideAPlaceholder;
  if (side === 'b' && match.sideBPlaceholder) return match.sideBPlaceholder;
  return 'TBD';
}

function ReviewMatchCard({
  match,
  participantsById,
  courtNamesById,
}: {
  match: DrawMatchListItem;
  participantsById: Map<string, PlayEventParticipant>;
  courtNamesById: Record<string, string>;
}) {
  const courtLabel = match.courtId
    ? (courtNamesById[match.courtId] ?? 'Court assigned')
    : 'Court TBD';
  const sideALabel = getReviewSideLabel(match, 'a', participantsById);
  const sideBLabel = getReviewSideLabel(match, 'b', participantsById);
  const sideAIsPlaceholder =
    match.sideAIsFeeder || (match.sideAIds.length === 0 && sideALabel !== 'TBD');
  const sideBIsPlaceholder =
    match.sideBIsFeeder || (match.sideBIds.length === 0 && sideBLabel !== 'TBD' && sideBLabel !== 'Bye');

  if (match.isBye || match.result === 'bye') {
    return (
      <article className="rounded-lg border border-border bg-card p-3 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          {match.matchLabel ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {match.matchLabel}
            </span>
          ) : null}
          <span className="text-[10px] text-muted-foreground">{courtLabel}</span>
        </div>
        <p className="mt-2 text-sm font-medium text-foreground">
          {sideALabel !== 'TBD' ? sideALabel : sideBLabel} · Bye
        </p>
      </article>
    );
  }

  return (
    <article className="rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        {match.matchLabel ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {match.matchLabel}
          </span>
        ) : null}
        <span className="truncate text-[10px] text-muted-foreground">{courtLabel}</span>
      </div>
      <div className="space-y-1.5 text-sm">
        <div
          className={cn(
            'rounded-md px-2 py-1.5',
            sideAIsPlaceholder
              ? 'border border-dashed border-muted-foreground/30 italic text-muted-foreground'
              : 'border border-border bg-muted/30 font-medium text-foreground'
          )}
        >
          {sideALabel}
        </div>
        <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          vs
        </p>
        <div
          className={cn(
            'rounded-md px-2 py-1.5',
            sideBIsPlaceholder
              ? 'border border-dashed border-muted-foreground/30 italic text-muted-foreground'
              : 'border border-border bg-muted/30 font-medium text-foreground'
          )}
        >
          {sideBLabel}
        </div>
      </div>
    </article>
  );
}

function RoundColumnsDiagram({
  preview,
  event,
  participantNamesById,
  participantsById,
  courtNamesById,
  courtAssignmentsByKey,
}: PlayEventDrawReviewDiagramProps) {
  const matchesByRound = useMemo(() => {
    const allMatches = buildPreviewDrawMatchListItems({
      preview,
      event,
      participantNamesById,
      courtAssignmentsByKey,
    });
    const grouped = new Map<number, DrawMatchListItem[]>();
    for (const match of allMatches) {
      const existing = grouped.get(match.roundNumber) ?? [];
      existing.push(match);
      grouped.set(match.roundNumber, existing);
    }
    return grouped;
  }, [courtAssignmentsByKey, event, participantNamesById, preview]);

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex min-w-max gap-4">
        {preview.rounds.map((round) => {
          const roundMatches = matchesByRound.get(round.roundNumber) ?? [];
          const sittingOut = round.sittingOut
            .map((id) => participantsById.get(id))
            .filter((participant): participant is PlayEventParticipant => Boolean(participant));

          return (
            <section
              key={round.roundNumber}
              className="flex w-56 shrink-0 flex-col gap-3"
            >
              <div className="text-center">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Round {round.roundNumber}
                </h4>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  {roundMatches.length} match{roundMatches.length === 1 ? '' : 'es'}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {roundMatches.map((match) => (
                  <ReviewMatchCard
                    key={match.id}
                    match={match}
                    participantsById={participantsById}
                    courtNamesById={courtNamesById}
                  />
                ))}
              </div>
              {sittingOut.length > 0 ? (
                <p className="rounded-md border border-dashed border-border bg-muted/20 px-2 py-1.5 text-[11px] text-muted-foreground">
                  Sitting out:{' '}
                  {sittingOut
                    .map((participant) => getPlayEventParticipantDisplayName(participant))
                    .join(', ')}
                </p>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function PlayEventDrawReviewDiagram({
  event,
  preview,
  allocationMode,
  participantsById,
  participantNamesById,
  courtNamesById,
  courtAssignmentsByKey,
}: PlayEventDrawReviewDiagramProps) {
  const assignedCourtCount = Object.values(courtAssignmentsByKey).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mode</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {allocationMode === 'auto' ? 'Auto allocate' : 'Manual placement'}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rounds</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{preview.rounds.length}</p>
        </div>
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Matches</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{preview.totalMatches}</p>
        </div>
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Courts set</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {assignedCourtCount > 0 ? assignedCourtCount : 'Auto on generate'}
          </p>
        </div>
      </div>

      {preview.byeCount > 0 || preview.unassignedParticipantIds.length > 0 ? (
        <div className="flex flex-wrap gap-2 text-sm">
          {preview.byeCount > 0 ? (
            <span className="rounded-full bg-muted px-3 py-1 text-muted-foreground">
              {preview.byeCount} bye{preview.byeCount === 1 ? '' : 's'}
            </span>
          ) : null}
          {preview.unassignedParticipantIds.length > 0 ? (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              {preview.unassignedParticipantIds.length} player(s) unassigned
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-xl border border-border bg-muted/10 p-4 sm:p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-foreground">Matchup overview</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {event.format === 'single_elimination'
              ? 'Bracket projection — later rounds fill in as winners are decided.'
              : 'All rounds at a glance before you generate the draw.'}
          </p>
        </div>

        {event.format === 'single_elimination' ? (
          <PlayEventBracketTree
            matches={flattenDrawPreviewMatches(preview)}
            participantNamesById={participantNamesById}
          />
        ) : (
          <RoundColumnsDiagram
            event={event}
            preview={preview}
            allocationMode={allocationMode}
            participantsById={participantsById}
            participantNamesById={participantNamesById}
            courtNamesById={courtNamesById}
            courtAssignmentsByKey={courtAssignmentsByKey}
          />
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {event.format === 'social_rotate'
          ? 'Starting the event will publish all rounds and open the match draw for further edits.'
          : 'Generating the draw will create all rounds. You can still edit upcoming rounds before they start.'}
      </p>
    </div>
  );
}
